/**
 * Process-lifetime business boot session.
 * Logout always clears warm memory — next login always loads from storage.
 */

import { normalizeBusinessStorageId } from '../../utils/sharedCore';

/** @typedef {{ inFlight: boolean, done: boolean, promise: Promise<unknown>|null, sessionKey: string|null, emptyBucketRecoveredFor: string|null, memoryHot: boolean, generation: number }} PageBoot */

/** @type {PageBoot} */
export const pageBoot = {
  inFlight: false,
  done: false,
  promise: null,
  sessionKey: null,
  emptyBucketRecoveredFor: null,
  /** True after a successful entity load in this process (cleared on logout). */
  memoryHot: false,
  /**
   * Bumped on every hard reset / logout so in-flight load completions cannot
   * re-heat memory and skip the next login's storage load (path stuck loading).
   */
  generation: 0
};

export function normalizeBootSessionKey(businessId) {
  if (businessId == null || businessId === '') return '';
  try {
    return String(normalizeBusinessStorageId(businessId) || businessId);
  } catch {
    return String(businessId);
  }
}

/**
 * Hard clear — next login must load.
 * @param {{ allowAuthLoad?: boolean }} [options]
 *   allowAuthLoad: done=true so authStateChanged is not stuck on skipped_boot
 */
export function resetBusinessBootstrapSession(options = {}) {
  pageBoot.generation += 1;
  pageBoot.inFlight = false;
  pageBoot.done = options.allowAuthLoad === true;
  pageBoot.promise = null;
  pageBoot.sessionKey = null;
  pageBoot.emptyBucketRecoveredFor = null;
  pageBoot.memoryHot = false;
}

/**
 * @deprecated Soft-logout warm keep is disabled — always hard-resets.
 * Kept so existing call sites compile.
 */
export function markBootstrapSoftLogout() {
  resetBusinessBootstrapSession({ allowAuthLoad: true });
}

/**
 * @param {string|number|null|undefined} businessId
 * @param {number} [expectedGeneration] — if provided, ignore stale completions after logout
 */
export function markBootstrapLoadSucceeded(businessId, expectedGeneration) {
  if (
    expectedGeneration != null &&
    expectedGeneration !== pageBoot.generation
  ) {
    return false;
  }
  const key = normalizeBootSessionKey(businessId);
  pageBoot.inFlight = false;
  pageBoot.done = true;
  if (key) {
    pageBoot.sessionKey = key;
    pageBoot.memoryHot = true;
  }
  return true;
}

export function isWarmBusinessMemory(businessId) {
  // Warm re-login skip disabled — always treat memory as cold for login decisions.
  void businessId;
  return false;
}

/**
 * Before linking a login session: always prepare a fresh load.
 * @returns {{ reuseWarmMemory: boolean, businessSwitched: boolean }}
 */
export function prepareBootstrapForLogin(businessId) {
  const nextKey = normalizeBootSessionKey(businessId);
  const prevKey = pageBoot.sessionKey;
  const switched = Boolean(prevKey && nextKey && prevKey !== nextKey);

  resetBusinessBootstrapSession({ allowAuthLoad: true });

  void import('../../services/completeLogoutService')
    .then((m) => m.bumpLogoutClearEpoch(switched ? 'business-switch' : 'login'))
    .catch(() => {});

  void import('./clearBusinessWarmCaches')
    .then((m) => m.clearBusinessWarmCaches(prevKey || nextKey || null))
    .catch(() => {});
  void import('../../domain/dashboard/derivedModelCache')
    .then((m) => m.clearDerivedDashboardModelCaches())
    .catch(() => {});
  void import('../navigation/routeKeepAlive')
    .then((m) => m.teardownAllRouteKeepAlives(switched ? 'business-switch' : 'login'))
    .catch(() => {});

  return { reuseWarmMemory: false, businessSwitched: switched };
}

export function getWarmBusinessSessionKey() {
  return pageBoot.memoryHot && pageBoot.sessionKey ? pageBoot.sessionKey : null;
}

export function getBootstrapGeneration() {
  return pageBoot.generation;
}
